/**
 * Approving a load in bulk.
 *
 * Approval is not a status write. `PENDING` loads go to the dedicated approve
 * endpoint, which needs the warehouse, fulfillment method and (for pallet
 * products) the pallet size that an admin would otherwise pick by hand in the
 * row form. Everything those fields need is already on the row, so this module
 * derives the request body and — just as importantly — reports when it *can't*,
 * so the confirmation can say which loads still need opening individually
 * instead of firing requests the API will reject.
 */

/**
 * Statuses that reach `APPROVED` through the approve endpoint rather than the
 * status endpoint.
 *
 * `WAITLIST` is deliberately excluded. Those loads exist because stock was
 * short when the order was placed, and the row form makes the admin choose a
 * fulfillment method explicitly before it will let them through — a judgement
 * call that shouldn't be made for 26 loads at once.
 */
export const BULK_APPROVE_SOURCES = ['PENDING'];

export function isBulkApproveSource(status: string): boolean {
  return BULK_APPROVE_SOURCES.includes(status);
}

/**
 * Request body for `POST /organization/order-items/:orderId/:itemId/approve`.
 *
 * A type alias rather than an interface on purpose. `BulkStatusTarget.approve`
 * is typed `Record<string, unknown>` — the API layer only forwards this as a
 * JSON body and deliberately does not import it — and TypeScript gives an
 * implicit index signature to object type aliases but not to interfaces, so as
 * an interface this could not be assigned there.
 */
export type BulkApprovePayload = {
  locationId: string;
  loadOptionId: string;
  palletSizeId?: string;
  deliveryFeeOverrideCents?: number;
  carrierRate?: number;
  pickupNote?: string;
  deliveryNote?: string;
  ticketNumber?: string;
  internalNotes?: string;
};

interface ApprovableRow {
  status?: string;
  quantity?: unknown;
  location?: { id?: string } | null;
  reservedLocation?: { id?: string } | null;
  orgLoadOptionIdSnapshot?: string | null;
  palletIdSnapshot?: string | null;
  deliveryFeeSnapshot?: number | null;
  carrierRate?: number | null;
  pickupNote?: string | null;
  deliveryNote?: string | null;
  ticketNumber?: string | null;
  internalNotes?: string | null;
  product?: { deliveryType?: string | null } | null;
  choices?: { locations?: { id: string }[] } | null;
}

/**
 * Picks the warehouse to approve against.
 *
 * The approve endpoint refuses a warehouse with no stock row for the product,
 * so a warehouse is only usable if it appears in the row's location choices —
 * those come straight from the product's stock rows. When the load already
 * names a warehouse that isn't stocked, this gives up rather than silently
 * shipping from somewhere else; reassigning a warehouse is the admin's call.
 */
function resolveLocationId(row: ApprovableRow): string | null {
  const stocked = row.choices?.locations ?? [];
  const isStocked = (id: string) => stocked.some((location) => location.id === id);

  const assigned = row.location?.id ?? row.reservedLocation?.id ?? null;
  if (assigned) return isStocked(assigned) ? assigned : null;

  // Nothing assigned yet: only safe when there is no choice to make.
  return stocked.length === 1 ? stocked[0].id : null;
}

/**
 * Builds the approve request for one load, or returns null when the row is
 * missing something the endpoint requires.
 *
 * Fields the admin never touched are omitted rather than sent as null, because
 * the endpoint writes whatever it is given — passing blanks would clear notes
 * and ticket numbers that are already on the load.
 */
export function buildBulkApprovePayload(row: ApprovableRow): BulkApprovePayload | null {
  const locationId = resolveLocationId(row);
  const loadOptionId = row.orgLoadOptionIdSnapshot ?? null;

  if (!locationId || !loadOptionId) return null;

  const payload: BulkApprovePayload = { locationId, loadOptionId };

  if (row.product?.deliveryType === 'PALLET') {
    if (!row.palletIdSnapshot) return null;
    payload.palletSizeId = row.palletIdSnapshot;
  }

  // `deliveryFeeSnapshot` on a PENDING load holds the address x warehouse
  // carrier rate in CENTS — checkout writes `carrierRateCents` into it verbatim.
  // (Approved loads store the same field in dollars; only pending rows reach
  // this code, so reading it as cents is safe here and keeps the rate the
  // customer ordered against.)
  const feeCents = row.deliveryFeeSnapshot;
  if (typeof feeCents === 'number' && Number.isFinite(feeCents)) {
    payload.deliveryFeeOverrideCents = Math.max(0, Math.round(feeCents));
  }

  if (typeof row.carrierRate === 'number' && Number.isFinite(row.carrierRate)) {
    payload.carrierRate = Math.max(0, Math.round(row.carrierRate));
  }

  if (row.pickupNote) payload.pickupNote = row.pickupNote;
  if (row.deliveryNote) payload.deliveryNote = row.deliveryNote;
  if (row.ticketNumber) payload.ticketNumber = row.ticketNumber;
  if (row.internalNotes) payload.internalNotes = row.internalNotes;

  return payload;
}

/**
 * The single routing decision for a bulk status change: returns an approve body
 * when this row must go through the approve endpoint to reach `targetStatus`,
 * and null when the status endpoint handles it.
 *
 * Status matters as much as the target here. Sending an already-dispatched load
 * back to `APPROVED` is a status write — it clears the BOL trail — whereas a
 * pending load has never been approved at all. Only the latter belongs on the
 * approve endpoint, so both the plan and the request builder ask this.
 */
export function bulkApproveBodyFor(
  row: ApprovableRow,
  targetStatus: string
): BulkApprovePayload | null {
  if (targetStatus !== 'APPROVED') return null;
  if (!isBulkApproveSource(row.status ?? '')) return null;

  return buildBulkApprovePayload(row);
}
